import { useState, useCallback, useRef } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  threshold?: number;
  preventDefaultOnSwipe?: boolean;
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

export const useSwipeGesture = ({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  preventDefaultOnSwipe = false
}: SwipeGestureOptions = {}) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | 'up' | 'down' | null>(null);
  const touchStartRef = useRef<TouchPoint | null>(null);
  const touchEndRef = useRef<TouchPoint | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchEndRef.current = null;
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };
    setIsSwiping(true);
    setSwipeDirection(null);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };

    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = touchEndRef.current.y - touchStartRef.current.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Determine swipe direction during movement for visual feedback
    if (absX > absY && absX > threshold / 2) {
      setSwipeDirection(deltaX > 0 ? 'right' : 'left');
      if (preventDefaultOnSwipe) {
        e.preventDefault();
      }
    } else if (absY > absX && absY > threshold / 2) {
      setSwipeDirection(deltaY > 0 ? 'down' : 'up');
    }
  }, [threshold, preventDefaultOnSwipe]);

  const onTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !touchEndRef.current) {
      setIsSwiping(false);
      setSwipeDirection(null);
      return;
    }

    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = touchEndRef.current.y - touchStartRef.current.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const timeDelta = touchEndRef.current.time - touchStartRef.current.time;
    
    // Only trigger if swipe is fast enough (under 500ms) or distance is significant
    const isValidSwipe = timeDelta < 500 || absX > threshold * 2 || absY > threshold * 2;

    if (isValidSwipe) {
      // Horizontal swipe takes precedence if it's larger
      if (absX > absY && absX > threshold) {
        if (deltaX > 0) {
          onSwipeRight?.();
        } else {
          onSwipeLeft?.();
        }
      }
      // Vertical swipe
      else if (absY > absX && absY > threshold) {
        if (deltaY > 0) {
          onSwipeDown?.();
        } else {
          onSwipeUp?.();
        }
      }
    }

    setIsSwiping(false);
    setSwipeDirection(null);
    touchStartRef.current = null;
    touchEndRef.current = null;
  }, [threshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  const getSwipeOffset = useCallback(() => {
    if (!touchStartRef.current || !touchEndRef.current) return { x: 0, y: 0 };
    return {
      x: touchEndRef.current.x - touchStartRef.current.x,
      y: touchEndRef.current.y - touchStartRef.current.y
    };
  }, []);

  return {
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd
    },
    isSwiping,
    swipeDirection,
    getSwipeOffset
  };
};

export default useSwipeGesture;
